// Value/time formatting defaults for chart labels (no DOM, unit-tested).
// Callers with domain formatting (display units, degrees, etc.) pass their
// own formatter; these are the sensible defaults the components fall back to.

export interface FormatValueOptions {
  /** Fixed decimals; default adapts to magnitude (100→0, 10→1, else 1). */
  decimals?: number;
  /** Compact 12.4K/1.2M above 10 000 (default true). */
  compact?: boolean;
}

export function formatValue(v: number, opts: FormatValueOptions = {}): string {
  if (!Number.isFinite(v)) return '—';
  const compact = opts.compact ?? true;
  const abs = Math.abs(v);
  if (compact && abs >= 10_000) {
    const [scaled, suffix] = abs >= 1_000_000 ? [v / 1_000_000, 'M'] : [v / 1_000, 'K'];
    return `${trimZero(scaled.toFixed(Math.abs(scaled) >= 100 ? 0 : 1))}${suffix}`;
  }
  const decimals = opts.decimals ?? (abs >= 100 ? 0 : 1);
  return trimZero(v.toFixed(decimals));
}

function trimZero(s: string): string {
  const cleaned = s.includes('.') ? s.replace(/\.?0+$/, '') : s;
  return cleaned === '-0' ? '0' : cleaned;
}

/** "14:32" (locale-shaped) — crosshair readouts, sub-hour ticks. */
export function formatClock(t: number, locale?: string): string {
  return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(t);
}

/** Axis tick label for a window span: clock inside two days, date beyond. */
export function formatTickTime(t: number, spanMs: number, locale?: string): string {
  if (spanMs <= 48 * 3_600_000) return formatClock(t, locale);
  if (spanMs <= 14 * 86_400_000) {
    return new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric' }).format(t);
  }
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(t);
}

/** Tooltip time: enough context to be unambiguous within the window. */
export function formatPointTime(t: number, spanMs: number, locale?: string): string {
  if (spanMs <= 26 * 3_600_000) {
    return new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      hour: 'numeric',
      minute: '2-digit',
    }).format(t);
  }
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(t);
}

/** Short weekday for day bands ("Wed"): sentence case, as v2 sets every label. */
export function formatDayLabel(t: number, locale?: string): string {
  return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(t);
}

/**
 * A threshold line's label. A latest value past the limit is named in words,
 * because the chart line and the alarm red are too close in hue to say it
 * (cloud/docs/web-refresh-v2.md, "Charts"; TOM-194).
 */
export function thresholdLabel(
  label: string,
  valueText: string,
  past: boolean,
  direction: 'above' | 'below'
): string {
  const base = `${label} ${valueText}`;
  return past ? `${base} · now ${direction}` : base;
}

/** Rough text width in px for collision math (Inter at a given size). */
export function estimateTextWidth(text: string, fontSizePx: number): number {
  return text.length * fontSizePx * 0.58;
}
